import React, { FormEvent, useState } from 'react';
import { Alert, Button, Input, Text } from 'barte-design-system';
import { PrototypeRole, PrototypeRoleProvider, useRoleSession } from '../../access/role';
import './AccessCodeGate.css';

// `REACT_APP_ACCESS_CODE` é o código do papel CLIENTE — o nome sem sufixo é herança da
// versão de código único e foi mantido porque o CI já injeta essa variável.
// `.trim()` nas duas pontas: a entrada do usuário é aparada, então um valor injetado pelo
// CI com espaço sobrando quebraria a detecção de colisão sem quebrar o login — quem tem só
// o código de cliente entraria como interno.
const CLIENT_CODE = (process.env.REACT_APP_ACCESS_CODE || '').trim() || 'barte-testes';

// O fallback do código interno vale só em desenvolvimento, de propósito: se a variável não
// for configurada no CI, produção sobe SEM código interno (ninguém entra como interno) em
// vez de aceitar um valor que está versionado neste repositório.
const CONFIGURED_INTERNAL_CODE =
  (process.env.REACT_APP_ACCESS_CODE_INTERNAL || '').trim() ||
  (process.env.NODE_ENV === 'development' ? 'barte-proto' : '');

// Duas variáveis com o mesmo valor é a pior configuração possível: o interno é checado
// primeiro, então TODO participante de teste entraria como interno — navegação completa e
// comentários — e o papel cliente sumiria sem sinal nenhum. Na dúvida, nega o privilégio:
// o interno fica indisponível até alguém corrigir a variável, e o cliente segue cliente.
// O aviso não é limitado a development de propósito: a colisão nasce das variáveis do CI,
// ou seja, num build de produção.
const CODES_COLLIDE =
  CONFIGURED_INTERNAL_CODE !== '' && CONFIGURED_INTERNAL_CODE === CLIENT_CODE;

if (CODES_COLLIDE) {
  console.warn(
    '[AccessCodeGate] REACT_APP_ACCESS_CODE e REACT_APP_ACCESS_CODE_INTERNAL têm o mesmo ' +
      'valor. O acesso interno foi desabilitado para não conceder privilégio a quem tem o ' +
      'código de cliente.'
  );
}

const INTERNAL_CODE = CODES_COLLIDE ? '' : CONFIGURED_INTERNAL_CODE;
const INTERNAL_ENABLED = INTERNAL_CODE !== '';

function roleForCode(code: string): PrototypeRole | null {
  if (INTERNAL_CODE !== '' && code === INTERNAL_CODE) return 'internal';
  if (code === CLIENT_CODE) return 'client';
  return null;
}

interface AccessCodeGateProps {
  children: React.ReactNode;
}

export function AccessCodeGate({ children }: AccessCodeGateProps) {
  // Sem `internalEnabled`, um browser que desbloqueou como interno antes da configuração
  // quebrar continuaria interno: `readStoredRole` lê do localStorage sem passar por
  // `roleForCode`. O rebaixamento não é persistido — corrigida a variável, o browser volta
  // a ser interno sozinho.
  const { role, unlock, logout } = useRoleSession({ internalEnabled: INTERNAL_ENABLED });
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = code.trim();
    const matched = trimmed === '' ? null : roleForCode(trimmed);
    if (matched) {
      unlock(matched);
      setCode('');
      return;
    }
    setError(true);
  }

  if (role) {
    return (
      <PrototypeRoleProvider role={role} onLogout={logout}>
        {children}
      </PrototypeRoleProvider>
    );
  }

  return (
    <div className="access-code-gate">
      <form className="access-code-card" onSubmit={handleSubmit}>
        <Text variant="label-lg">Barte Protótipos</Text>
        <Text variant="body-sm" className="access-code-subtitle">
          Informe o código de acesso para continuar.
        </Text>

        {error && (
          <Alert
            variant="error"
            title="Código inválido"
            description="Verifique o código e tente novamente."
            showCloseButton={false}
          />
        )}

        <Input
          type="password"
          label="Código de acesso"
          placeholder="Digite o código"
          value={code}
          error={error}
          onChange={(event) => {
            setCode((event.target as HTMLInputElement).value);
            setError(false);
          }}
        />

        <Button type="submit">Entrar</Button>
      </form>
    </div>
  );
}
